/**
 * Feature hook: Open document in IFS Cloud (Aurena).
 * Supports both IFS documents (All Documents) and OneDrive/working documents (Working Documents).
 * Dispatches getDocumentUrl or getDocumentUrlFromOneDrive; dialog state is from the slice.
 */
import { useCallback, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../hooks";
import {
  getDocumentUrl,
  getDocumentUrlFromOneDrive,
  closeDocumentUrlDialog,
} from "../slices/documents/documentUrlSlice";
import { ON_SUCCESS_MESSAGE_INVOKE, ORIGINAL_DOC_TYPE } from "../constants/actionTypes";
import { SUCCESS_TYPE } from "../constants/messageTypes";

const LINK_PLATFORM_IFS_AURENA = "linkToIFSAurena";
const LINK_PLATFORM_IFS_CLOUD_ALL = "linkToIFSCloudAllRevisions";

function isOneDriveItem(item) {
  return item != null && (item.id != null || item.documentId != null);
}

function getDocumentTitle(item) {
  const rawTitle =
    item?.Title ??
    item?.title ??
    item?.Name ??
    item?.name ??
    item?.docName ??
    item?.displayName ??
    item?.fileName ??
    "";

  if (!rawTitle) {
    return "Document";
  }

  try {
    return decodeURIComponent(String(rawTitle));
  } catch {
    return String(rawTitle);
  }
}

export function useOpenInIFS() {
  const dispatch = useAppDispatch();
  const documentUrl = useAppSelector((state) => state.DocumentUrl);
  const lastOpenedDocumentTitleRef = useRef("Document");
  const prevIsOpenIfsSuccessRef = useRef(false);
  const hasInitiatedOpenIfsRef = useRef(false);

  const isOpenIfsSuccess =
    documentUrl?.statusType === "success" &&
    documentUrl?.dialogType === "info";

  useEffect(() => {
    const becameSuccess = isOpenIfsSuccess && !prevIsOpenIfsSuccessRef.current;

    if (becameSuccess && hasInitiatedOpenIfsRef.current) {
      dispatch({
        type: ON_SUCCESS_MESSAGE_INVOKE,
        payload: {
          type: SUCCESS_TYPE,
          title: "Opened IFS Cloud successfully",
          message: `${lastOpenedDocumentTitleRef.current} opened successfully!`,
        },
      });
      hasInitiatedOpenIfsRef.current = false;
    }

    if (!isOpenIfsSuccess && documentUrl?.statusType !== "pending") {
      hasInitiatedOpenIfsRef.current = false;
    }

    prevIsOpenIfsSuccessRef.current = isOpenIfsSuccess;
  }, [isOpenIfsSuccess, documentUrl?.statusType, dispatch]);

  const openInIFS = useCallback(
    async (item, options = {}) => {
      if (!item) return;
      hasInitiatedOpenIfsRef.current = true;
      lastOpenedDocumentTitleRef.current = getDocumentTitle(item);
      if (isOneDriveItem(item)) {
        const result = await dispatch(
          getDocumentUrlFromOneDrive({
            item,
            docType: ORIGINAL_DOC_TYPE,
            linkPlatform: options.linkPlatform ?? LINK_PLATFORM_IFS_CLOUD_ALL,
            aurenaShowDetails: options.aurenaShowDetails ?? true,
          })
        );
        if (getDocumentUrlFromOneDrive.fulfilled.match(result) && result.payload != null) {
          const url = typeof result.payload === "string" ? result.payload : result.payload?.[0];
          if (url != null && url !== "") {
            window.open(String(url), "_blank");
          }
        }
      } else {
        const itemWithCapitals = {
          ...item,
          DocNo: item.docNo ?? item.DocNo,
          DocRev: item.docRev ?? item.DocRev,
          DocClass: item.class ?? item.DocClass,
          DocSheet: item.sheetNo ?? item.DocSheet,
        };
        const result = await dispatch(
          getDocumentUrl({
            items: itemWithCapitals,
            docType: ORIGINAL_DOC_TYPE,
            linkPlatform: options.linkPlatform ?? LINK_PLATFORM_IFS_CLOUD_ALL,
            aurenaShowDetails: options.aurenaShowDetails ?? true,
          })
        );
        if (getDocumentUrl.fulfilled.match(result) && result.payload != null) {
          const url = Array.isArray(result.payload)
            ? result.payload[0]
            : result.payload;
          if (url != null) {
            window.open(String(url), "_blank");
          }
        }
      }
    },
    [dispatch]
  );

  return {
    openInIFS,
    openIfsLoading: documentUrl?.loading ?? false,
    openIfsDialogOpen:
      (documentUrl?.isDialogOpen ?? false) && !isOpenIfsSuccess,
    openIfsDialogType: documentUrl?.dialogType ?? "error",
    openIfsDialogTitle: documentUrl?.dialogTitle ?? "",
    openIfsDialogMessage: documentUrl?.dialogMessage ?? "",
    openIfsDialogDetails: documentUrl?.dialogDetails ?? "",
    closeOpenIfsDialog: () => dispatch(closeDocumentUrlDialog()),
  };
}
